// components/Loader.js
import React, {  useRef } from 'react';

const Loader = () => {
  const mountRef = useRef(null);
  
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-black" ref={mountRef}>
      <div className="flex flex-col items-center">
        {/* Main loader animation */}
        <div className="loader relative mb-8">
          <svg width="120" height="120" viewBox="0 0 120 120">
            {/* Outer ring - red with dash animation */}
            <circle
              cx="60"
              cy="60"
              r="54"
              fill="none"
              stroke="#ef4444"
              strokeWidth="4"
              className="dash"
            />
            
            {/* Middle ring - spinning with gradient */}
            <circle
              cx="60"
              cy="60"
              r="42"
              fill="none"
              stroke="url(#gradient)"
              strokeWidth="6"
              className="spin"
            />
            
            {/* Inner ring - counter rotating */}
            <circle
              cx="60"
              cy="60"
              r="32"
              fill="none"
              stroke="#ff6b6b"
              strokeWidth="3"
              strokeDasharray="5 5"
              className="counter-spin"
            />
            
            {/* 3D Printer Nozzle Icon */}
            <g className="animate-nozzle" transform="translate(60, 60)">
              <path
                d="M-10,-15 L10,-15 L5,-5 L5,5 L3,15 L-3,15 L-5,5 L-5,-5 Z"
                fill="#ef4444"
                stroke="#fff"
                strokeWidth="1"
              />
              {/* Printing effect - animated dots */}
              <circle cx="0" cy="20" r="2" fill="#ef4444" className="dot dot-1" />
              <circle cx="0" cy="20" r="2" fill="#ef4444" className="dot dot-2" />
              <circle cx="0" cy="20" r="2" fill="#ef4444" className="dot dot-3" />
            </g>
            
            {/* Gradient definitions */}
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ef4444" />
                <stop offset="50%" stopColor="#dc2626" />
                <stop offset="100%" stopColor="#ef4444" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        
        {/* Loading text with typewriter effect */}
        <div className="text-2xl font-bold text-red-500 mb-4">
          <span className="inline-block">3D</span>
          <span className="inline-block mx-1 text-white">PrintTech</span>
          <span className="inline-block animate-pulse">.</span>
          <span className="inline-block animate-pulse delay-100">.</span>
          <span className="inline-block animate-pulse delay-200">.</span>
        </div>
        
        {/* Animated status text */}
        <div className="text-sm text-gray-400 animate-fade-in-out">
          <span className="status-text"></span>
        </div>
      </div>
      
      {/* 3D layers being printed */}
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2">
        <div className="layers-container">
          <div className="layer layer-1"></div>
          <div className="layer layer-2"></div>
          <div className="layer layer-3"></div>
        </div>
      </div>
      
      <style jsx>{`
        .dash {
          animation: dashArray 2s ease-in-out infinite,
            dashOffset 2s linear infinite;
        }
        
        .spin {
          animation: spinDashArray 2s ease-in-out infinite,
            spin 8s ease-in-out infinite,
            dashOffset 2s linear infinite;
          transform-origin: center;
        }
        
        .counter-spin {
          animation: counter-spin 10s linear infinite;
          transform-origin: center;
        }
        
        .animate-nozzle {
          animation: nozzle-move 3s ease-in-out infinite;
        }
        
        .dot {
          opacity: 0;
          animation: dot-fall 1.5s ease-in infinite;
        }
        
        .dot-1 {
          animation-delay: 0s;
        }
        
        .dot-2 {
          animation-delay: 0.5s;
        }
        
        .dot-3 {
          animation-delay: 1s;
        }
        
        .delay-100 {
          animation-delay: 0.3s;
        }
        
        .delay-200 {
          animation-delay: 0.6s;
        }
        
        .layers-container {
          width: 100px;
          height: 50px;
          position: relative;
        }
        
        .layer {
          position: absolute;
          width: 100%;
          height: 2px;
          background: #ef4444;
          opacity: 0;
          animation: layer-build 2s ease-in-out infinite;
        }
        
        .layer-1 {
          bottom: 10px;
          animation-delay: 0s;
        }
        
        .layer-2 {
          bottom: 20px;
          animation-delay: 0.5s;
        }
        
        .layer-3 {
          bottom: 30px;
          animation-delay: 1s;
        }
        
        .status-text::after {
          content: "Initializing...";
          animation: status-change 6s linear infinite;
        }
        
        @keyframes dashArray {
          0% {
            stroke-dasharray: 0 1 359 0;
          }
          50% {
            stroke-dasharray: 0 359 1 0;
          }
          100% {
            stroke-dasharray: 359 1 0 0;
          }
        }
        
        @keyframes spinDashArray {
          0% {
            stroke-dasharray: 270 90;
          }
          50% {
            stroke-dasharray: 0 360;
          }
          100% {
            stroke-dasharray: 270 90;
          }
        }
        
        @keyframes dashOffset {
          0% {
            stroke-dashoffset: 365;
          }
          100% {
            stroke-dashoffset: 5;
          }
        }
        
        @keyframes spin {
          0% {
            rotate: 0deg;
          }
          12.5%,
          25% {
            rotate: 270deg;
          }
          37.5%,
          50% {
            rotate: 540deg;
          }
          62.5%,
          75% {
            rotate: 810deg;
          }
          87.5%,
          100% {
            rotate: 1080deg;
          }
        }
        
        @keyframes counter-spin {
          from {
            rotate: 0deg;
          }
          to {
            rotate: -360deg;
          }
        }
        
        @keyframes nozzle-move {
          0%, 100% {
            transform: translate(0, 0);
          }
          25% {
            transform: translate(5px, 0);
          }
          50% {
            transform: translate(0, 5px);
          }
          75% {
            transform: translate(-5px, 0);
          }
        }
        
        @keyframes dot-fall {
          0% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(20px);
          }
        }
        
        @keyframes layer-build {
          0% {
            width: 0;
            opacity: 0;
          }
          50% {
            width: 100%;
            opacity: 1;
          }
          100% {
            width: 100%;
            opacity: 0.3;
          }
        }
        
        @keyframes status-change {
          0% {
            content: "Initializing...";
          }
          33% {
            content: "Loading models...";
          }
          66% {
            content: "Preparing interface...";
          }
          100% {
            content: "Almost ready...";
          }
        }
        
        @keyframes fade-in-out {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
        }
        
        .animate-fade-in-out {
          animation: fade-in-out 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Loader;